import clsx from 'clsx';
import { CalendarDays, CalendarOff, ExternalLink, FileText, Info, Pin, ServerCog, ShieldAlert, Timer, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnnouncementPriority, AnnouncementType } from '@pz/core';
import type { ProcOutput } from '@pz/server';
import { d, dLong } from '../lib/format';
import { useM } from '../lib/hooks';
import { Button, Pill, useToast, type Tone } from './ui';

export type AnnouncementView = ProcOutput<'announcements.mine'>['announcements'][number];

/** Symbol and tone per type: the type tells at a glance whether it is a date, an instruction or plain information. */
export const TYPE_META: Record<AnnouncementType, { icon: LucideIcon; tone: Tone }> = {
  info: { icon: Info, tone: 'info' },
  event: { icon: CalendarDays, tone: 'accent' },
  closure: { icon: CalendarOff, tone: 'warn' },
  deadline: { icon: Timer, tone: 'warn' },
  safety: { icon: ShieldAlert, tone: 'bad' },
  it: { icon: ServerCog, tone: 'neutral' },
  policy: { icon: FileText, tone: 'neutral' },
};

export const PRIORITY_TONE: Record<AnnouncementPriority, Tone> = { normal: 'neutral', important: 'warn', critical: 'bad' };

const EDGE: Record<AnnouncementPriority, string> = { normal: 'border-l-line-strong', important: 'border-l-warn', critical: 'border-l-bad' };

/** Only web links the publisher typed; the server refuses everything else, and the client does not trust it blindly. */
function safeHref(url: string | null): string | null {
  return url && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

export function TypePill({ type }: { type: AnnouncementType }) {
  const { t } = useTranslation();
  const m = TYPE_META[type];
  const Icon = m.icon;
  return (
    <Pill tone={m.tone}>
      <Icon className="size-3" /> {t(`ann.types.${type}`)}
    </Pill>
  );
}

/** When it happens: "Fr, 24.10.2026, 15:00" for dated announcements. */
export function eventText(a: Pick<AnnouncementView, 'eventDate' | 'eventTime'>): string | null {
  if (!a.eventDate) return null;
  return `${dLong(a.eventDate)}${a.eventTime ? `, ${a.eventTime}` : ''}`;
}

/**
 * One announcement. Opening the text marks it as read (read receipts count how many people saw it); an announcement
 * that asks for a confirmation (safety instruction) needs the button "Zur Kenntnis genommen".
 */
export function AnnouncementCard({ a, compact }: { a: AnnouncementView; compact?: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(!compact);
  const mark = useM('announcements.markRead', { invalidate: ['announcements.', 'home.', 'notifications.'] });
  const long = a.body.length > 240 || a.body.split('\n').length > 4;
  const unread = a.requiresAck ? !a.acked : !a.read;
  const href = safeHref(a.linkUrl);
  const when = eventText(a);

  const read = () => {
    if (!a.read && a.occurrence) mark.mutate({ id: a.id, occurrence: a.occurrence, revision: a.revision });
  };
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !a.requiresAck) read();
  };
  const confirm = () => {
    if (!a.occurrence) return;
    mark.mutate({ id: a.id, occurrence: a.occurrence, ack: true, revision: a.revision }, { onSuccess: () => toast.ok(t('ann.confirmed')), onError: (e) => toast.error(e) });
  };

  return (
    <article className={clsx('rounded-lg border border-l-[3px] border-line bg-sheet px-3.5 py-3', EDGE[a.priority], unread && 'bg-accent-soft/25')}>
      <header className="flex flex-wrap items-center gap-1.5">
        <TypePill type={a.type} />
        {a.priority !== 'normal' && <Pill tone={PRIORITY_TONE[a.priority]}>{t(`ann.priorities.${a.priority}`)}</Pill>}
        {a.pinned && (
          <span className="inline-flex items-center gap-0.5 text-xs text-graphite" title={t('ann.pinnedHint')}>
            <Pin className="size-3" /> {t('ann.pinned')}
          </span>
        )}
        {unread && <Pill tone="accent">{a.requiresAck ? t('ann.needsConfirm') : t('ann.new')}</Pill>}
        <span className="ml-auto text-[11px] text-faint">
          {a.author ? `${a.author} · ` : ''}
          {d(a.startsOn)}
        </span>
      </header>
      <h3 className="mt-1.5 text-[15px] leading-snug font-semibold">{a.title}</h3>
      {when && (
        <p className="num mt-0.5 flex items-center gap-1 text-sm text-ink">
          <CalendarDays className="size-3.5 text-graphite" /> {when}
        </p>
      )}
      {a.body && (
        <div className="mt-1.5">
          <p className={clsx('text-sm whitespace-pre-line text-graphite', !open && 'line-clamp-3')}>{a.body}</p>
          {long && (
            <button type="button" className="mt-0.5 text-xs font-medium text-accent hover:underline" onClick={toggle}>
              {open ? t('ann.less') : t('ann.more')}
            </button>
          )}
        </div>
      )}
      <footer className="mt-2 flex flex-wrap items-center gap-2">
        {href && (
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={read}>
            <Button size="sm" icon={<ExternalLink className="size-3.5" />}>
              {a.linkLabel || t('ann.openLink')}
            </Button>
          </a>
        )}
        {a.requiresAck && !a.acked && (
          <Button size="sm" variant="primary" onClick={confirm} loading={mark.isPending}>
            {t('ann.confirm')}
          </Button>
        )}
        {a.requiresAck && a.acked && <span className="text-xs text-good">{t('ann.confirmedShort')}</span>}
        {!a.requiresAck && !a.read && (
          <Button size="sm" variant="ghost" onClick={read} loading={mark.isPending}>
            {t('ann.markRead')}
          </Button>
        )}
      </footer>
    </article>
  );
}
